import React, { useEffect, useMemo, useState } from "react";

import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    Stack,
} from "@mui/material";

import DataTable from "react-data-table-component";
import ReactDatePicker from "react-datepicker";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import PageContainer from "../../../../components/atoms/PageContainer";
import {
    dateToString,
    stringToDate,
    substractDateAsString,
} from "../../../../Constant/datefns";
import { getInputValueOnChange } from "../../../../helpers/form";
import { DateRange } from "../../../../types/app/date";
import ExpandableChartRow from "./ExpandableChartRow";
import { createLandingPagesListColumns } from "./LandingPageReportsPage.helpers";
import { LandingPageReportsPageProps } from "./LandingPageReportsPage.types";

function LandingPageReportsPage({
    isReportsLoading,
    landingPageReports,
    language,
    isVisitsLoading,
    landingPageVisits,
    dispatchGetLandingPageVisits,
    dispatchGetLandingPageReports,
    dispatchClearLandingPageReports,

    isDetailLoading,
    landingPageDetailVisits,
    dispatchGetLandingPageDetailVisits,
}: LandingPageReportsPageProps) {
    const [dateRange, setDateRange] = useState<DateRange>(DateRange.empty);

    const [startDateString, setStartDateString] = useState<string>("");
    const [endDateString, setEndDateString] = useState<string>("");

    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    useEffect(() => {
        const defaultStart = substractDateAsString(7);
        const defaultEnd = dateToString(new Date());

        setStartDateString(defaultStart);
        setEndDateString(defaultEnd);

        // 1) CHART
        dispatchGetLandingPageReports({
            start_date: defaultStart,
            end_date: defaultEnd,
        });

        // 2) TABLE
        dispatchGetLandingPageVisits({
            start_date: defaultStart,
            end_date: defaultEnd,
        });

        return () => {
            dispatchClearLandingPageReports();
        };
    }, [
        dispatchGetLandingPageReports,
        dispatchGetLandingPageVisits,
        dispatchClearLandingPageReports,
    ]);

    useEffect(() => {
        if (dateRange === DateRange.empty) {
            return;
        }

        const daysNum = Number(dateRange);
        const newStart = substractDateAsString(daysNum);
        const newEnd = dateToString(new Date());

        setStartDateString(newStart);
        setEndDateString(newEnd);

        // 1) CHART
        dispatchGetLandingPageReports({
            start_date: newStart,
            end_date: newEnd,
        });

        // 2) TABLE
        dispatchGetLandingPageVisits({
            start_date: newStart,
            end_date: newEnd,
        });
    }, [
        dateRange,
        dispatchGetLandingPageReports,
        dispatchGetLandingPageVisits,
    ]);

    function handleStartDateChange(dateObj: Date | null) {
        setDateRange(DateRange.empty);
        if (!dateObj) {
            setStartDateString("");
            return;
        }
        const str = dateToString(dateObj);
        setStartDateString(str);
    }

    function handleEndDateChange(dateObj: Date | null) {
        setDateRange(DateRange.empty);
        if (!dateObj) {
            setEndDateString("");
            return;
        }
        const str = dateToString(dateObj);
        setEndDateString(str);
    }

    useEffect(() => {
        if (!startDateString || !endDateString) return;

        dispatchGetLandingPageReports({
            start_date: startDateString,
            end_date: endDateString,
        });
        dispatchGetLandingPageVisits({
            start_date: startDateString,
            end_date: endDateString,
        });
    }, [
        startDateString,
        endDateString,
        dispatchGetLandingPageReports,
        dispatchGetLandingPageVisits,
    ]);

    const chartData = useMemo(() => {
        if (!landingPageReports) return [];
        return landingPageReports.map((item) => ({
            date: item.date,
            value: item.total_visit,
        }));
    }, [landingPageReports]);

    const columns = useMemo(() => {
        return createLandingPagesListColumns({
            onDetailClick: (row) => {
                if (startDateString && endDateString) {
                    dispatchGetLandingPageDetailVisits({
                        id: row.id,
                        start_date: startDateString,
                        end_date: endDateString,
                    });
                }
            },
        });
    }, [startDateString, endDateString, dispatchGetLandingPageDetailVisits]);

    const loadingAny = isReportsLoading || isVisitsLoading;

    const startDateValue: Date | null = useMemo(
        () => stringToDate(startDateString),
        [startDateString],
    );
    const endDateValue: Date | null = useMemo(
        () => stringToDate(endDateString),
        [endDateString],
    );

    return (
        <PageContainer
            title="Reports"
            variant="list"
            subtitle="On this page, you can view landing page analytics."
            loading={loadingAny}
        >
            <>
                <Box paddingTop={2}>
                    {/* Üst Filtre: Tarih Seçme + Date Range DropDown */}
                    <Stack direction="row" spacing={3} alignItems="center">
                        {/* Start Date */}
                        <div className="mui-datepicker">
                            <label>
                                {language?.tracker_detail?.start_date ??
                                    "Start Date"}
                            </label>
                            <ReactDatePicker
                                className="pl-1 mr-2 datePicker"
                                selected={startDateValue} // Date | null
                                onChange={(date) => handleStartDateChange(date)}
                                selectsStart
                                placeholderText="dd/mm/yyyy"
                                dateFormat="yyyy-MM-dd"
                                maxDate={endDateValue || new Date()}
                                disabled={isReportsLoading}
                            />
                            <DateRangeOutlinedIcon />
                        </div>

                        {/* End Date */}
                        <div className="mui-datepicker">
                            <label>
                                {language?.tracker_detail?.end_date ??
                                    "End Date"}
                            </label>
                            <ReactDatePicker
                                className="pl-1 datePicker"
                                selected={endDateValue}
                                onChange={(date) => handleEndDateChange(date)}
                                selectsEnd
                                placeholderText="dd/mm/yyyy"
                                dateFormat="yyyy-MM-dd"
                                minDate={startDateValue || undefined}
                                maxDate={new Date()}
                                disabled={isReportsLoading}
                            />
                            <DateRangeOutlinedIcon />
                        </div>

                        {/* OR */}
                        <div>
                            <span className="or-text">
                                {language?.tracker_detail?.or ?? "OR"}
                            </span>
                        </div>

                        {/* Date Range (Select) */}
                        <div>
                            <FormControl>
                                <Select
                                    label={
                                        language?.tracker_detail?.date_range ??
                                        "Date Range"
                                    }
                                    value={dateRange}
                                    onChange={(e) =>
                                        setDateRange(getInputValueOnChange(e))
                                    }
                                    style={{ width: 200 }}
                                    disabled={isReportsLoading}
                                >
                                    <MenuItem value={DateRange.empty}>
                                        {language?.reports?.select_days ??
                                            "Select Days"}
                                    </MenuItem>
                                    <MenuItem value={DateRange.yesterday}>
                                        {language?.reports?.yesterday ??
                                            "Yesterday"}
                                    </MenuItem>
                                    <MenuItem value={DateRange.today}>
                                        {language?.reports?.today ?? "Today"}
                                    </MenuItem>
                                    <MenuItem value={DateRange.last7Days}>
                                        {language?.reports?.last_7_days ??
                                            "Last 7 days"}
                                    </MenuItem>
                                    <MenuItem value={DateRange.last28Days}>
                                        {language?.reports?.last_28_days ??
                                            "Last 28 days"}
                                    </MenuItem>
                                    <MenuItem value={DateRange.last3Months}>
                                        {language?.reports?.last_90_days ??
                                            "Last 90 days"}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </Stack>

                    {/* Grafik Alanı */}
                    <Card className="mt-4">
                        <CardContent>
                            <div style={{ width: "100%", height: "400px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={chartData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke="#82ca9d"
                                            name="Total Visit"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </Box>
                <Card className="mt-4">
                    <CardContent>
                        <DataTable
                            columns={columns}
                            data={landingPageVisits || []}
                            highlightOnHover
                            persistTableHead
                            pointerOnHover
                            responsive
                            expandableRows
                            expandableRowsComponent={ExpandableChartRow}
                            onRowExpandToggled={(expanded, row) => {
                                if (expanded) {
                                    setExpandedRow(row.id);

                                    if (startDateString && endDateString) {
                                        dispatchGetLandingPageDetailVisits({
                                            id: row.id,
                                            start_date: startDateString,
                                            end_date: endDateString,
                                        });
                                    }
                                } else {
                                    setExpandedRow(null);
                                }
                            }}
                            expandableRowExpanded={(row) =>
                                row.id === expandedRow
                            }
                        />
                    </CardContent>
                </Card>
            </>
        </PageContainer>
    );
}

export default LandingPageReportsPage;
